import { get, post, put } from '@api/api';
import API_ROUTES from '@api/routes';
import {
	QueryClient,
	useMutation,
	useQuery,
	useQueryClient
} from '@tanstack/react-query';
import {
	IExportedFlow,
	IFlow,
	IFlowDisableStateUpdateRequest,
	IFlowScreen,
	IFlowScreens,
	IFlowsMetadata,
	IFlowsResponse,
	IFlowTheme,
	IFlowUpdateRequest,
	Task,
	Tasks,
	TaskType
} from 'types/flows';
import QueryKeys from '.';

const getFlows = async () => {
	const res = await get<IFlowsResponse>(API_ROUTES.v1.flows.all);
	return res.data;
};

const getFlowsMetadata = async () => {
	const res = await get<IFlowsMetadata>(API_ROUTES.v1.flows.metadata);
	return res.data;
};

const getScreens = (flowId: string) => async () => {
	const res = await get<IFlowScreens>(API_ROUTES.v1.flows.flow(flowId).screens);
	return res.data;
};

const setFlowsTheme = async (body: IFlowTheme) => {
	const res = await put<IFlowTheme>(API_ROUTES.v1.flows.theme.all, body);
	return res.data;
};

const getFlowsTheme = async () => {
	const res = await get<IFlowTheme>(API_ROUTES.v1.flows.theme.all);
	return res.data;
};

const isScreenAvailable = (flowId: string) => async () => {
	const res = await get(API_ROUTES.v1.flows.flow(flowId).screenAvailable);
	return res.data;
};

const updateFlowQuery = (queryClient: QueryClient, data: IFlow) => {
	const res = queryClient.getQueryData<IFlowsResponse>([QueryKeys.flows]) || {
		flows: [data],
		total: 1
	};
	if (res) {
		res.flows = res.flows.map((f) => (f.id === data.id ? data : f));
	}
	queryClient.setQueryData([QueryKeys.flows], res);
};

interface IImportFlowRequest extends IFlowUpdateRequest {
	flowId: string;
}

export const exportFlow = async (flowId: string) => {
	const res = await get<IExportedFlow>(
		API_ROUTES.v1.flows.flow(flowId).download
	);
	return res.data;
};

export const useImportFlow = () => {
	const queryClient = useQueryClient();
	return useMutation<IFlow, unknown, IImportFlowRequest>(
		async (value) => {
			const res = await post<IImportFlowRequest, IFlow>(
				API_ROUTES.v1.flows.upload,
				value
			);
			return res.data;
		},
		{
			onSuccess: async (data) => {
				updateFlowQuery(queryClient, data);
				const screens = await getScreens(data.id)();
				queryClient.setQueryData([QueryKeys.screens, data.id], screens);
			}
		}
	);
};

export const exportTheme = async () => {
	const res = await get<IFlowTheme>(API_ROUTES.v1.flows.theme.download);
	return res.data;
};

export const useImportTheme = () => {
	const queryClient = useQueryClient();
	return useMutation<IFlowTheme, unknown, IFlowTheme>(
		async (value) => {
			const res = await post<IFlowTheme, IFlowTheme>(
				API_ROUTES.v1.flows.theme.upload,
				value
			);
			return res.data;
		},
		{
			onSuccess: async (data, variables) => {
				queryClient.setQueryData([QueryKeys.flowsTheme], {
					...data,
					cssTemplate: variables.cssTemplate
				});
			}
		}
	);
};

export const getScreensInUse = (
	tasks: Tasks,
	currScreensState: IFlowScreens
) => {
	const readyScreens: IFlowScreen[] = [];
	Object.values(tasks).forEach((task: Task) => {
		if (task.type !== TaskType.screen || !task.screenId) {
			return;
		}
		const existingScreen = currScreensState[task.screenId];
		if (existingScreen) {
			readyScreens.push(existingScreen);
		}
	});
	return readyScreens;
};

export const useFlow = (flowId?: string) => {
	const queryClient = useQueryClient();

	return useQuery([QueryKeys.flows], getFlows, {
		staleTime: Infinity,
		retry: false,
		select: (res) =>
			flowId ? res.flows.find((f) => f.id === flowId) : undefined,
		onSuccess: () => {
			// we want to prefetch the theme to avoid flickering when entering the screen editor
			queryClient.prefetchQuery([QueryKeys.flowsTheme], getFlowsTheme);
		}
	});
};

export const useFlows = (enabled = true) =>
	useQuery([QueryKeys.flows], getFlows, { staleTime: 100000, enabled });

export const useFlowsMetadata = () =>
	useQuery([QueryKeys.flowsMetadata], getFlowsMetadata, {
		staleTime: Infinity
	});

export const useFlowTheme = () =>
	useQuery([QueryKeys.flowsTheme], getFlowsTheme, { staleTime: Infinity });

export const useUpdateFlowTheme = () => {
	const queryClient = useQueryClient();
	return useMutation<IFlowTheme, unknown, IFlowTheme>(
		async (value) => setFlowsTheme(value),
		{
			onSuccess: async (data, variables) => {
				/**
				 * because the logo URL does not change, the theme object reference is not changing
				 * this causes the dirty state to remain after saving
				 * another issue it causes, is that on save, the image is flickering (when there was no logo) or not updating (when there was another logo)
				 *
				 * In order to solve both, we are using the request payload, but getting the updated metadata from the response
				 */

				queryClient.setQueryData([QueryKeys.flowsTheme], {
					...data,
					cssTemplate: variables.cssTemplate
				});
			}
		}
	);
};

export const useFlowScreens = (flowId: string) =>
	useQuery([QueryKeys.screens, flowId], getScreens(flowId), {
		staleTime: Infinity
	});

export const useUpdateFlow = () => {
	const queryClient = useQueryClient();
	return useMutation<IFlow, unknown, IFlowUpdateRequest>(
		async (value) => {
			const res = await put<IFlowUpdateRequest, IFlow>(
				API_ROUTES.v1.flows.all,
				value
			);
			return res.data;
		},
		{
			onSuccess: async (data) => {
				updateFlowQuery(queryClient, data);
				const screens = await getScreens(data.id)();
				queryClient.setQueryData([QueryKeys.screens, data.id], screens);
			}
		}
	);
};

export const useUpdateFlowDisableState = () => {
	const queryClient = useQueryClient();
	return useMutation<
		IFlow,
		unknown,
		IFlowDisableStateUpdateRequest & { flowId: string }
	>(
		async (value) => {
			const res = await put<IFlowDisableStateUpdateRequest, IFlow>(
				API_ROUTES.v1.flows.flow(value.flowId).disable,
				value
			);
			return res.data;
		},
		{
			onSuccess: async (data) => updateFlowQuery(queryClient, data)
		}
	);
};

export const useScreenAvailable = (flowId: string, enabled: boolean) =>
	useQuery(['screenAvailable', flowId], isScreenAvailable(flowId), {
		enabled,
		retryDelay: 800,
		retry: (failureCount, error) =>
			failureCount <= 200 && (error as any)?.statusCode !== 200,
		refetchOnWindowFocus: false
	});
